import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Fade,
  FormHelperText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { saveNotesThunk } from '../../features/selectedVideo/selectedVideoSlice';
import { RootState } from '../../reducers';
import { Note, NoteDialogProps } from '../../types/notes';

const MAX_NOTE_LENGTH = 1000;

const NoteDialog: React.FC<NoteDialogProps> = ({
  open,
  onClose,
  existingNote,
  timestamp,
}) => {
  const dispatch = useDispatch();
  const [noteText, setNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { _id: fileId, textData } = useSelector(
    (state: RootState) =>
      state.selectedVideo.file || { _id: '', textData: { notes: [] } }
  );
  const notes = textData?.notes || [];

  const validateNote = (text: string): boolean => {
    if (!text.trim()) {
      setError('Note cannot be empty');
      return false;
    }
    if (text.length > MAX_NOTE_LENGTH) {
      setError(`Note cannot exceed ${MAX_NOTE_LENGTH} characters`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateNote(noteText) || !fileId) return;

    try {
      setIsSaving(true);
      const newNote: Note = {
        text: noteText,
        timestamp: existingNote ? existingNote.timestamp : timestamp,
      };

      const updatedNotes = existingNote
        ? notes.filter((note) => note.timestamp !== existingNote.timestamp)
        : [...notes];

      const insertIndex = updatedNotes.findIndex(
        (note) => note.timestamp > newNote.timestamp
      );

      if (insertIndex === -1) {
        updatedNotes.push(newNote);
      } else {
        updatedNotes.splice(insertIndex, 0, newNote);
      }

      await dispatch(saveNotesThunk({ fileId, notes: updatedNotes }));
      onClose();
    } catch (err) {
      setError('Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (open && existingNote) {
      setNoteText(existingNote.text);
    } else {
      setNoteText('');
    }
    setError(null);
  }, [open, existingNote]);

  const handleDelete = async () => {
    if (!fileId || !existingNote) return;

    try {
      setIsSaving(true);
      const updatedNotes = notes.filter(
        (note) => note.timestamp !== existingNote.timestamp
      );

      await dispatch(saveNotesThunk({ fileId, notes: updatedNotes }));
      onClose();
    } catch (err) {
      setError('Failed to delete note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoteText(e.target.value);
    validateNote(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={200}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {existingNote ? 'Edit Note' : 'Add Note'}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 'auto' }}
        >
          {`${noteText.length}/${MAX_NOTE_LENGTH}`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Note"
          fullWidth
          multiline
          rows={4}
          value={noteText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          error={!!error}
          disabled={isSaving}
          placeholder="Add your note here... (Ctrl + Enter to save)"
          InputProps={{
            sx: { fontSize: '1rem', lineHeight: 1.5 },
          }}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            p: 1,
          }}
        >
          {existingNote && (
            <Button
              onClick={handleDelete}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={isSaving}
            >
              Delete
            </Button>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              startIcon={<CloseIcon />}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isSaving || !!error}
              startIcon={
                isSaving ? <CircularProgress size={20} /> : <CheckIcon />
              }
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NoteDialog;
